import { createFileRoute, Outlet } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Player routes accept query params injected by Infinity iframe embeds. Example:
 *   /player/:id?host=infinity&slxapi={...}&mode=evaluation&resetonload=true&disableTopbar=true
 *
 * All five params are declared explicitly below — the schema is the contract.
 * Declaring them in the parent `/player` route means every route under it
 * (redirect at /player/:id, /player/learning-activity/:id, /player/plenary/:id)
 * automatically inherits these params via TanStack Router's parent→child search merge,
 * without each child redeclaring them.
 *
 * Consumers:
 * - disableTopbar → useCustomUIConfigurations (hides the topbar)
 * - mode          → preview/evaluation flags in player components
 * - host          → QuestionFooter Infinity detection AND server-side routing
 *                    in modules/xapi-events/.../processEvent.ts (via Referer)
 * - resetonload   → QuestionFooter reset-on-load behavior
 * - slxapi        → server-side actor/endpoint context forwarded to
 *                    infinityevents.cet.ac.il (via Referer header)
 * - component     → the item's catalog id in the external (kata) system (720 sessions)
 * - registration  → the external session id, keys 720 state/tracking (720 sessions)
 *
 * The server reads X-Client-Referrer (set to window.location.href by apiClient)
 * to extract host and slxapi from the original Infinity URL.
 *
 * Boolean-or-string unions: TanStack Router parses `disableTopbar=true` with JSON
 * semantics (boolean true), while callers building URLs manually may pass the
 * string 'true'. Consumers normalise both forms. We do NOT use z.preprocess to
 * coerce: that would make TanStack Router re-serialize the URL with quotes
 * (`disableTopbar="true"`), mangling the URL Infinity originally sent.
 *
 * IMPORTANT: Do NOT use .passthrough() here. It broadens the global useSearch
 * return type via TanStack Router's type system, cascading `unknown` types
 * into every useSearch({ strict: false }) consumer across the codebase.
 */
export const Route = createFileRoute('/player')({
  validateSearch: z.object({
    // Infinity iframe embed params (see doc block above)
    disableTopbar: z.union([z.boolean(), z.string()]).optional(),
    host: z.string().optional(),
    mode: z.string().optional(),
    resetonload: z.union([z.boolean(), z.string()]).optional(),
    slxapi: z.record(z.unknown()).optional(),
    // 720 external session: catalog id + session id, used to key kata state/tracking.
    component: z.string().optional(),
    registration: z.string().optional(),
    // Course context: set when a learning activity is opened from a course,
    // read by LearningObjectAuthorization to scope the access check.
    courseDraftId: z.string().optional()
  }),
  component: PlayerLayout
});

function PlayerLayout() {
  return <Outlet />;
}
